/**
 * Dynamic CSS Loader Service
 * Loads default.css from server and injects it into the document
 */

const CSS_ELEMENT_ID = 'dynamic-css';
const DEFAULT_CSS_URL = '/default.css';

/**
 * Fetch CSS content from server
 * @param {string} url - URL to fetch CSS from
 * @returns {Promise<string>} CSS content
 */
export const fetchCSS = async (url = DEFAULT_CSS_URL) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch CSS: ${response.status} ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error fetching CSS:', error);
    throw error;
  }
};

/**
 * Inject CSS content into document head
 * @param {string} cssContent - CSS content to inject
 */
export const injectCSS = (cssContent) => {
  try {
    // Remove existing dynamic CSS if present
    const existingStyle = document.getElementById(CSS_ELEMENT_ID);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create and inject new style element
    const styleElement = document.createElement('style');
    styleElement.id = CSS_ELEMENT_ID;
    styleElement.textContent = cssContent;
    document.head.appendChild(styleElement);

    return true;
  } catch (error) {
    console.error('Error injecting CSS:', error);
    throw error;
  }
};

/**
 * Apply fallback styles when CSS loading fails
 */
const applyFallbackStyles = () => {
  const fallbackCSS = `
    :root {
      --primary-color: #1976d2;
      --secondary-color: #dc004e;
      --background-color: #ffffff;
      --text-color: #333333;
      --font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
    }
    
    body {
      font-family: var(--font-family);
      color: var(--text-color);
      background-color: var(--background-color);
    }
  `;
  
  injectCSS(fallbackCSS);
  console.warn('Applied fallback CSS styles');
};

/**
 * Load dynamic CSS from server
 * @param {string} url - Optional custom URL to load CSS from
 * @returns {Promise<boolean>} Success status
 */
export const loadDynamicCSS = async (url = DEFAULT_CSS_URL) => {
  try {
    const cssContent = await fetchCSS(url);
    injectCSS(cssContent);
    
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load dynamic CSS, applying fallback:', error);
    applyFallbackStyles();
    return false;
  }
};

export default loadDynamicCSS;
